import React from "react";
import {
  Printer,
  FilePdf,
  FileXls,
  Copy,
  DownloadSimple,
  FileCsv,
} from "@phosphor-icons/react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Button } from "./ui/button";

const ExportDropdown = ({ data, columns, className }) => {
  const getCellValue = (row, column) => {
    const rawValue = row?.[column.headerName];

    if (typeof column.formatter === "function") {
      return column.formatter(rawValue, row);
    }

    return rawValue ?? "";
  };

  const handlePrint = (data, columns) => {
    const printWindow = window.open("", "_blank");
    const html = `
    <html>
      <head>
        <title>Print Table</title>
        <style>
          table { border-collapse: collapse; width: 100%; font-family: sans-serif; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background-color: #f2f2f2; }
        </style>
      </head>
      <body>
        <h2>Exported Data</h2>
        <table>
          <thead>
            <tr>
              ${columns
                .map((column) => `<th>${column.headerName}</th>`)
                .join("")}
            </tr>
          </thead>
          <tbody>
            ${data
              .map(
                (row) => `
              <tr>
                ${columns
                  .map((column) => `<td>${getCellValue(row, column)}</td>`)
                  .join("")}
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
      </body>
    </html>
  `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const exportToPDF = (data, columns) => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text("Table Data", 14, 22);

    const tableColumn = columns.map((column) => column.headerName);
    const tableRows = data.map((item) => {
      return columns.map((col) => getCellValue(item, col));
    });

    doc.autoTable({
      head: [tableColumn],
      body: tableRows,
      startY: 30,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [22, 163, 74] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 30 },
    });

    doc.save("table_data.pdf");
  };

  const exportToExcel = (data, columns, fileName) => {
    const worksheetRows = data.map((row) =>
      columns.reduce((acc, column) => {
        acc[column.headerName] = getCellValue(row, column);
        return acc;
      }, {})
    );
    const worksheet = XLSX.utils.json_to_sheet(worksheetRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, fileName);
  };

  const copyToClipboard = (data) => {
    const header = columns.map((c) => c.headerName).join("\t");
    const rows = data
      .map((row) => columns.map((c) => getCellValue(row, c)).join("\t"))
      .join("\n");

    const stringData = `${header}\n${rows}`;

    navigator.clipboard.writeText(stringData).then(
      () => {
        alert("Table data copied to clipboard");
      },
      (err) => {
        console.error("Could not copy text: ", err);
      }
    );
  };

  const exportToCSV = (data, fileName) => {
    const escapeCsv = (val) => {
      if (val === null || val === undefined) return "";
      const str = String(val);
      if (str.includes(",") || str.includes("\n") || str.includes('"')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const header = columns.map((c) => escapeCsv(c.headerName)).join(",");
    const rows = data
      .map((row) =>
        columns.map((c) => escapeCsv(getCellValue(row, c))).join(",")
      )
      .join("\n");

    const csvContent = `${header}\n${rows}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, fileName);
  };

  const handleExport = (type) => {
    const exportData = data.map((row) => {
      const rowData = {};
      columns.forEach((column) => {
        if (column.field !== "link") {
          let value;
          switch (column.field) {
            case "applicantName":
              const firstname = row.Applicant?.firstname || "";
              const lastname = row.Applicant?.lastname || "";
              value = row.fullName || `${firstname} ${lastname}`.trim();
              break;
            case "state":
              value = row.state || row.Applicant?.state || "";
              break;
            case "cos":
              value =
                row.course ||
                row.ApplicationsAcademicQualification?.course.name ||
                row.ApplicationNYSCDetails?.course?.name ||
                "";
              break;
            case "ittype":
              value =
                row.type ||
                row.vacancy?.vacancy_position?.name.toUpperCase() ||
                "";
              break;
            case "age":
              if (row.age) {
                value = row.age;
              } else if (row.Applicant?.date_of_birth) {
                const birthDate = new Date(row.Applicant?.date_of_birth);
                const currentYear = new Date().getFullYear();
                value = currentYear - birthDate.getFullYear();
              } else {
                value = "";
              }
              break;
            case "date":
              const rawDate = row.date || row.created_at;
              const dateValue = rawDate ? new Date(rawDate) : null;
              value =
                dateValue && !isNaN(dateValue)
                  ? dateValue.toLocaleDateString()
                  : "";
              break;
            case "companylocation":
            case "location":
              value = row.location || row.vacancy?.location?.name || "";
              break;
            case "businessunit":
            case "unit":
            case "sbu":
              value =
                row.unit || row.sbu || row.vacancy?.business_unit?.name || "";
              break;
            default:
              value = row[column.field];
              if (value === undefined && column.field.includes(".")) {
                value = column.field.split(".").reduce((o, i) => o?.[i], row);
              }
              value = value ?? "";
          }
          rowData[column.headerName] = value;
        }
      });
      return rowData;
    });

    switch (type) {
      case "print":
        handlePrint(exportData, columns);
        break;
      case "pdf":
        exportToPDF(exportData, columns);
        break;
      case "excel":
        exportToExcel(exportData, columns, "table_data.xlsx");
        break;
      case "copy":
        copyToClipboard(exportData);
        break;
      case "csv":
        exportToCSV(exportData, "table_data.csv");
        break;
      default:
        console.log("Invalid export type");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-8 gap-2 text-slate-600 border-dashed border-gray-300 bg-transparent hover:bg-slate-50 ${className}`}
        >
          <DownloadSimple size={16} />
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-white z-50">
        <DropdownMenuItem
          onClick={() => handleExport("print")}
          className="cursor-pointer"
        >
          <Printer size={16} className="mr-2 text-green-600" />
          <span>Print</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("pdf")}
          className="cursor-pointer"
        >
          <FilePdf size={16} className="mr-2 text-red-600" />
          <span>Export to PDF</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("excel")}
          className="cursor-pointer"
        >
          <FileXls size={16} className="mr-2 text-green-700" />
          <span>Export to Excel</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("copy")}
          className="cursor-pointer"
        >
          <Copy size={16} className="mr-2 text-blue-600" />
          <span>Copy to Clipboard</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport("csv")}
          className="cursor-pointer"
        >
          <FileCsv size={16} className="mr-2 text-green-600" />
          <span>Export to CSV</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportDropdown;
